import React, { useState, useEffect, useMemo } from "react";

import { useLocation, useNavigate } from "react-router-dom";
import {
  getFirestore,
  doc,
  getDoc,
  collection,
  addDoc,
  Timestamp,
} from "firebase/firestore";

import NavBar from "./NavBar";
import Footer from "./Footer";
import SkeletonCard from "./SkeletonCard";

import "../styles/checkout.css";

function stripHtml(html) {
  const tmp = document.createElement("DIV");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
}

function CheckoutPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const db = getFirestore();

  const rawParams = location.pathname.split("/checkout/")[1];
  const productPairs = useMemo(() => rawParams.split("~"), [rawParams]);

  const [contact, setContact] = useState("");
  const [shipping, setShipping] = useState({
    firstName: "",
    lastName: "",
    address: "",
    city: "",
    postal: "",
    email: "",
  });

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const fetchedProducts = await Promise.all(
          productPairs.map(async (pair) => {
            const [collection, docId, quantity = 1, color] = pair.split("/");
            const docRef = doc(db, collection, docId);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
              let selectedColor;
              if (color !== "orignal") {
                const data = docSnap.data();
                const productColors = data.productColors || [];
                selectedColor = productColors.find((c) => c.color === color);
              }

              return {
                ...docSnap.data(),
                id: docId,
                quantity: Number(quantity),
                selectedColor,
                collection,
              };
            }
            return null;
          })
        );
        setProducts(fetchedProducts.filter((item) => item));
      } catch (error) {
        console.error("Failed to fetch products:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [db, productPairs]);

  const isFormValid =
    contact &&
    shipping.firstName &&
    shipping.lastName &&
    shipping.address &&
    shipping.city &&
    shipping.email;

  const DELIVERY_CHARGE = 250;

  const subtotal = products.reduce(
    (sum, item) => sum + item.productPrice * item.quantity,
    0
  );

  const total = subtotal + DELIVERY_CHARGE;

  const sendOrderEmails = async ({ shipping, products, subtotal, total }) => {
    const stripHtml = (html) => {
      return html.replace(/<[^>]+>/g, "");
    };

    const generateEmailHTML = (
      heading,
      message,
      includeCustomerInfo = false
    ) => {
      return `
  <div style="font-family: 'Segoe UI', sans-serif; background-color: #f4f6f8; padding: 20px;">
    <div style="max-width: 600px; margin: 0 auto; background-color: #ffffff; padding: 32px; border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.05);">
      <h1 style="color: #333333; font-size: 24px; margin-bottom: 10px;">${heading}</h1>
      <p style="color: #555555; font-size: 16px; line-height: 1.6;">${message}</p>

      <hr style="margin: 24px 0; border: none; border-top: 1px solid #e0e0e0;" />

      <h2 style="font-size: 18px; color: #222222; margin-bottom: 16px;">Order Summary</h2>

      ${products
        .map(
          (product) => `
          <div style="display: flex; align-items: flex-start; margin-bottom: 20px;">
            <img src="${
              product.selectedColor?.image || product.productImage
            }" alt="${
            product.productTitle
          }" style="width: 70px; height: 70px; object-fit: cover; border-radius: 8px; border: 1px solid #e0e0e0; margin-right: 16px;" />
            <div style="flex: 1;">
              <div style="font-size: 16px; font-weight: 600; color: #000000;">${
                product.productTitle
              }</div>
              <div style="font-size: 14px; color: #555555; margin-top: 4px;">
                ${stripHtml(product.productDescription).slice(0, 50)}${
            stripHtml(product.productDescription).length > 50 ? "..." : ""
          }
              </div>
              <div style="font-size: 13px; color: #777777; margin-top: 2px;">
                Color: ${product.selectedColor?.color || "Original"}
              </div>
              <div style="font-size: 14px; color: #000000; margin-top: 6px;">
                Quantity: ${product.quantity} &nbsp;&nbsp;|&nbsp;&nbsp; Rs ${
            product.productPrice * product.quantity
          } ${
            includeCustomerInfo
              ? `&nbsp;&nbsp;|&nbsp;&nbsp; Brand: ${
                  product.brand
                } &nbsp;&nbsp;|&nbsp;&nbsp; Link: <a href=${`https://www.libas.store/product/${product.collection}/${product.id}`} style="color: #007bff;">View Product</a>`
              : ""
          }
              </div>
            </div>
          </div>
        `
        )
        .join("")}

      <hr style="margin: 24px 0; border: none; border-top: 1px solid #e0e0e0;" />

      <div style="font-size: 15px; color: #333; margin-bottom: 6px;">
        <strong>Subtotal:</strong> Rs ${subtotal}
      </div>
      <div style="font-size: 15px; color: #333; margin-bottom: 6px;">
        <strong>Delivery:</strong> Rs ${DELIVERY_CHARGE}
      </div>
      <div style="font-size: 17px; font-weight: 700; color: #000000; margin-top: 12px;">
        <strong>Total:</strong> PKR ${total}
      </div>

${
  includeCustomerInfo
    ? `
      <hr style="margin: 32px 0 16px; border: none; border-top: 1px solid #e0e0e0;" />
      <h3 style="font-size: 18px; color: #333; margin: 24px 0 12px;">Customer Information</h3>
      <table style="font-size: 14px; color: #444; line-height: 1.6;">
        <tr>
          <td style="padding: 4px 8px;"><strong>Full Name:</strong></td>
          <td style="padding: 4px 8px;">${shipping.firstName} ${shipping.lastName}</td>
        </tr>
        <tr>
          <td style="padding: 4px 8px;"><strong>Email:</strong></td>
          <td style="padding: 4px 8px;">${shipping.email}</td>
        </tr>
        <tr>
          <td style="padding: 4px 8px;"><strong>Contact:</strong></td>
          <td style="padding: 4px 8px;">${contact}</td>
        </tr>
        <tr>
          <td style="padding: 4px 8px;"><strong>Address:</strong></td>
          <td style="padding: 4px 8px;">${shipping.address}, ${shipping.city}</td>
        </tr>
        <tr>
          <td style="padding: 4px 8px;"><strong>Postal Code:</strong></td>
          <td style="padding: 4px 8px;">${shipping.postal}</td>
        </tr>
      </table>
    `
    : ""
}

      <hr style="margin: 32px 0 16px;" />
      <div style="font-size: 12px; color: #888888; text-align: center;">
        Libas Store • Thank you for your order!
      </div>
    </div>
  </div>
`;
    };

    const brandNameMap = {
      fesishop: "FesiShop",
      resin: "RH110",
    };

    const brands = [
      ...new Set(
        products.map((p) => brandNameMap[p.brand?.toLowerCase()] || p.brand)
      ),
    ];
    const brandsString = brands.join(", ");

    const recipients = [
      {
        to: shipping.email,
        subject: "Order Placed - Libas Store",
        heading: "Libas Store - Order Confirmation",
        message:
          "Thank you for your purchase!<br/>We're getting your order ready to be shipped.",
      },
      {
        to: "mujtaba.dev21@gmail.com",
        subject: `Order Received from Libas Store - ${brandsString}`,
        heading: `Order Received - ${brandsString}`,
        message: "A new order has been placed on Libas Store.",
      },
      {
        to: "mfaisal9c@gmail.com",
        subject: `Order Received from Libas Store - ${brandsString}`,
        heading: `Order Received - ${brandsString}`,
        message: "A new order has been placed on Libas Store.",
      },
    ];

    try {
      for (const recipient of recipients) {
        const isAdmin = recipient.to !== shipping.email;
        const emailHtml = generateEmailHTML(
          recipient.heading,
          recipient.message,
          isAdmin
        );

        const res = await fetch(
          "https://resend-backend-fesirh110.vercel.app/api/send-email",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              to: recipient.to,
              subject: recipient.subject,
              html: emailHtml,
            }),
          }
        );

        if (!res.ok) {
          const errorText = await res.text();
          console.error(`Failed to send email to ${recipient.to}`, errorText);
        }
      }
    } catch (err) {
      console.error("Error sending emails:", err);
    }
  };

  const saveOrderToFirebase = async () => {
    const orderData = {
      contact,
      shipping,
      products: products.map((product) => ({
        productTitle: product.productTitle,
        productImage: product.selectedColor?.image || product.productImage,
        color: product.selectedColor?.color || "Original",
        productPrice: product.productPrice,
        quantity: product.quantity,
        brand: product.brand || "N/A",
        link: `https://www.libas.store/product/${product.collection}/${product.id}`,
      })),
      createdAt: Timestamp.now(),
    };

    try {
      const docRef = await addDoc(collection(db, "orders"), orderData);
      console.log("Order saved with ID:", docRef.id);
      return docRef.id;
    } catch (error) {
      console.error("Error saving order:", error);
      throw error;
    }
  };

  const handleOrder = async (e) => {
    e.preventDefault();
    alert("Order placed successfully!");
    await sendOrderEmails({
      shipping,
      products,
      subtotal,
      total,
    });
    await saveOrderToFirebase();
    navigate("/");
  };

  return (
    <>
      <NavBar />
      <div className="page-bg">
        <div className="main-wrap">
          <form className="left" onSubmit={handleOrder} autoComplete="off">
            <div className="section-title">Contact</div>
            <div className="mb-3">
              <input
                type="text"
                className="form-control text-dark"
                placeholder="Email or mobile phone number"
                value={contact}
                onChange={(e) => setContact(e.target.value)}
                required
              />
            </div>
            <div className="section-title">Delivery</div>
            <div className="row">
              <div className="col-6 mb-3">
                <input
                  type="text"
                  className="form-control text-dark"
                  placeholder="First name"
                  value={shipping.firstName}
                  onChange={(e) =>
                    setShipping({ ...shipping, firstName: e.target.value })
                  }
                  required
                />
              </div>
              <div className="col-6 mb-3">
                <input
                  type="text"
                  className="form-control text-dark"
                  placeholder="Last name"
                  value={shipping.lastName}
                  onChange={(e) =>
                    setShipping({ ...shipping, lastName: e.target.value })
                  }
                  required
                />
              </div>
            </div>
            <div className="mb-3">
              <input
                type="text"
                className="form-control text-dark"
                placeholder="Address"
                value={shipping.address}
                onChange={(e) =>
                  setShipping({ ...shipping, address: e.target.value })
                }
                required
              />
            </div>
            <div className="row">
              <div className="col-6 mb-3">
                <input
                  type="text"
                  className="form-control text-dark"
                  placeholder="City"
                  value={shipping.city}
                  onChange={(e) =>
                    setShipping({ ...shipping, city: e.target.value })
                  }
                  required
                />
              </div>
              <div className="col-6 mb-3">
                <input
                  type="text"
                  className="form-control text-dark"
                  placeholder="Postal code (optional)"
                  value={shipping.postal}
                  onChange={(e) =>
                    setShipping({ ...shipping, postal: e.target.value })
                  }
                />
              </div>
            </div>
            <div className="mb-3">
              <input
                type="email"
                className="form-control text-dark"
                placeholder="Email"
                value={shipping.email}
                onChange={(e) =>
                  setShipping({ ...shipping, email: e.target.value })
                }
                required
              />
            </div>
            <div className="section-title">Payment Method</div>
            <div className="mb-2" style={{ color: "#888", fontSize: 14 }}>
              All transactions are secure and encrypted.
            </div>
            <div className="mb-3">
              <button
                type="button"
                className="p-3 border-1 rounded w-100 text-dark text-start h6"
              >
                Cash on Delivery (COD)
              </button>
            </div>
            <hr className="divider" />
            <button
              type="submit"
              className="btn btn-outline-dark w-100"
              style={{ fontWeight: 600, fontSize: 18, padding: "12px 0" }}
              disabled={!isFormValid}
            >
              Complete order
            </button>
          </form>

          <div className="right">
            <div
              style={{
                fontWeight: 600,
                fontSize: 20,
                marginBottom: 24,
                color: "#000",
              }}
            >
              Order summary
            </div>
            {loading
              ? Array.from({ length: 1 }).map((_, index) => (
                  <SkeletonCard key={index} type="checkout" />
                ))
              : products.map((product, index) => (
                  <div
                    key={index}
                    className="summary-row"
                    style={{
                      position: "relative",
                      alignItems: "flex-start",
                    }}
                  >
                    <div style={{ position: "relative", marginRight: 12 }}>
                      <div
                        style={{
                          width: 70,
                          height: 70,
                          borderRadius: 12,
                          overflow: "hidden",
                          backgroundColor: "#f9f9f9",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          border: "1px solid #e5e5e5",
                        }}
                      >
                        <img
                          src={
                            product.selectedColor?.image || product.productImage
                          }
                          alt={product.productTitle}
                          style={{
                            maxWidth: "100%",
                            maxHeight: "100%",
                            objectFit: "contain",
                          }}
                        />
                      </div>
                      {product.quantity && (
                        <sup
                          style={{
                            position: "absolute",
                            top: -5,
                            right: -5,
                            backgroundColor: "#666666",
                            color: "#fff",
                            padding: "10px 7px",
                            borderRadius: "9999px",
                            fontSize: 12,
                            fontWeight: "bold",
                          }}
                        >
                          {product.quantity}
                        </sup>
                      )}
                    </div>

                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                        }}
                      >
                        <div
                          style={{
                            fontWeight: 500,
                            fontSize: 15,
                            color: "#000",
                          }}
                        >
                          {product.productTitle}
                        </div>
                        <div style={{ fontSize: 14, color: "#000" }}>
                          Rs {product.productPrice * product.quantity}
                        </div>
                      </div>

                      <div
                        style={{ color: "#000", fontSize: 13, marginTop: 2 }}
                      >
                        {stripHtml(product.productDescription).slice(0, 40)}
                        {stripHtml(product.productDescription).length > 40
                          ? "..."
                          : ""}
                      </div>

                      <div
                        style={{
                          color: "#666",
                          fontSize: 13,
                          marginTop: 2,
                        }}
                      >
                        {product.selectedColor?.color || "Original"}
                      </div>
                    </div>
                  </div>
                ))}
            <hr className="divider" />
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: 15,
                marginBottom: 8,
                color: "#000",
              }}
            >
              <span>Subtotal</span>
              <span>Rs {subtotal}</span>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: 15,
                marginBottom: 8,
                color: "#000",
              }}
            >
              <span>Delivery</span>
              <span>Rs {DELIVERY_CHARGE}</span>
            </div>
            <div className="total-row">
              <span>Total</span>
              <span>
                PKR <span style={{ fontSize: 20 }}>{total}</span>
              </span>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
}

export default CheckoutPage;
